/**
 * Console Logger Implementation
 *
 * Logs tracking events to browser console
 * Uses native console methods to ensure visibility in all environments (including loopback)
 */

/* eslint-disable no-console */

import type { Logger as ILogger } from './Logger';

import { LogLevel, type TrackingEvent } from '../types';

class ConsoleLogger implements ILogger {
  public readonly id = 'console-logger';

  public readonly name = 'Console Logger';

  private logLevel: LogLevel;

  private logQueue: TrackingEvent[] = [];

  private flushTimer?: ReturnType<typeof setTimeout>;

  constructor(logLevel: LogLevel = LogLevel.INFO) {
    this.logLevel = logLevel;
  }

  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  log(event: TrackingEvent): void {
    // Immediate logging for errors
    if (event.level === LogLevel.ERROR) {
      this.flushImmediate(event);

      return;
    }

    // Queue other events for batching
    this.logQueue.push(event);
    this.scheduleFlush();
  }

  logBatch(events: TrackingEvent[]): void {
    events.forEach((e) => this.log(e));
  }

  private flushImmediate(event: TrackingEvent): void {
    if (event.level !== undefined && event.level < this.logLevel) {
      return;
    }

    this.formatAndLog(event);
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      return;
    }

    this.flushTimer = setTimeout(() => {
      this.flushQueue();
      this.flushTimer = undefined;
    }, 100); // Batch logs every 100ms
  }

  private flushQueue(): void {
    if (this.logQueue.length === 0) {
      return;
    }

    const eventsToLog = this.logQueue.filter(
      (e) => (e.level ?? LogLevel.INFO) >= this.logLevel
    );

    eventsToLog.forEach((e) => this.formatAndLog(e));
    this.logQueue = [];
  }

  // eslint-disable-next-line class-methods-use-this
  private formatAndLog(event: TrackingEvent): void {
    const level = event.level ?? LogLevel.INFO;
    const prefix = `[FormForge Troubleshooting] ${event.type}`;
    const timestamp = new Date(event.timestamp).toLocaleString();
    const duration = event.duration
      ? ` (Duration: ${ConsoleLogger.formatDuration(event.duration)})`
      : '';

    const logData = {
      Feature: event.feature,
      Time: timestamp,
      ...(duration && { Duration: duration }),
      Data: event.data,
      ...(event.metadata && { Metadata: event.metadata }),
      ...(event.correlationId && { CorrelationId: event.correlationId }),
      ...(event.parentEventId && { ParentEventId: event.parentEventId }),
    };

    // Use native console methods directly to ensure visibility in all environments
    // and avoid duplicate logs (Logger from @saviynt/common may also output to console)
    switch (level) {
      case LogLevel.ERROR:
        console.error(prefix, logData);
        break;
      case LogLevel.WARN:
        console.log(`⚠️ ${prefix}`, logData);
        break;
      case LogLevel.INFO:
        console.log(`ℹ️ ${prefix}`, logData);
        break;
      case LogLevel.DEBUG:
        console.log(`🔍 ${prefix}`, logData);
        break;
      default:
        console.log(prefix, logData);
    }
  }

  private static formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms.toFixed(2)}ms`;
    }

    return `${(ms / 1000).toFixed(2)}s`;
  }

  clear(): void {
    this.logQueue = [];

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
  }

  destroy(): void {
    this.clear();
  }
}

export default ConsoleLogger;
